import { useMutation, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";

import { api, changedElsewhere } from "../../api/client";
import type { Logistics, LogisticsAssumptions, Phase } from "../../api/types";
import { Badge, Button } from "../../components/ui";
import { formatNumber, t } from "../../i18n/i18n";
import { designateDossierReadOnly, useDossierLocked, useWriteBlock } from "../../sharing/access";
import { useUi } from "../../state/ui";

/** Hypothèses réglables du dossier, partagées par les vues Logistique (ravitaillement, drones) et Maintenance (DTO,
 *  CAPOPS) : même enregistrement (`ExerciseLogistics.assumptions`), même contrôle entre postes, même curseur. Chaque
 *  vue ne montre et ne remet aux valeurs par défaut que SES hypothèses (`keys`). */

const num = (value: number, digits = 1) => formatNumber(value, { minimumFractionDigits: digits, maximumFractionDigits: digits });

export type SliderSpec = { key: keyof LogisticsAssumptions; label: string; digits: number; unit?: string };

export function Slider({
  spec,
  value,
  bounds,
  disabled,
  onCommit,
}: {
  spec: SliderSpec;
  value: number;
  bounds: { min: number; max: number; step: number };
  disabled: boolean;
  onCommit: (value: number) => void;
}) {
  const locked = useDossierLocked();
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  const commit = () => {
    if (local !== value) onCommit(local);
  };
  return (
    <label className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1">
      <span className="text-meta text-ink-soft">
        {t(spec.label)}
        {spec.unit && <span className="text-ink-muted"> ({t(spec.unit)})</span>}
      </span>
      <span className="text-right text-meta font-semibold text-ink tabular-nums">{num(local, spec.digits)}</span>
      <input
        type="range"
        min={bounds.min}
        max={bounds.max}
        step={bounds.step}
        value={local}
        disabled={disabled}
        // Dossier en lecture seule pour le compte (docs/17 §3.2) : la valeur ne bouge pas, l'appui désigne le bandeau.
        aria-disabled={locked || undefined}
        onChange={(e) => (locked ? designateDossierReadOnly() : setLocal(Number(e.target.value)))}
        onPointerUp={commit}
        onKeyUp={commit}
        onBlur={commit}
        className="col-span-2 w-full accent-accent"
      />
    </label>
  );
}

/** Enregistrement des hypothèses : chaque réglage part avec la date des hypothèses affichées (modifiées entre-temps
 *  par un autre poste, elles sont relues au lieu d'être écrasées) ; refusé, le curseur revient à la valeur du serveur,
 *  jamais affiché comme enregistré (audit de persistance m11). `reverts` : clé des curseurs à remonter. */
export function useSaveAssumptions(
  exerciseId: string | null,
  phase: Phase,
  opo: number | undefined,
  query: UseQueryResult<Logistics, Error>,
  key: readonly unknown[],
) {
  const queryClient = useQueryClient();
  const notify = useUi((s) => s.notify);
  const [reverts, setReverts] = useState(0);
  const save = useMutation({
    mutationFn: (changes: Partial<LogisticsAssumptions>) =>
      api.updateLogisticsAssumptions(exerciseId as string, phase, { ...changes, base: query.data?.updated_at ?? null }, opo),
    onSuccess: (next: Logistics) => {
      queryClient.setQueryData(key, next);
      void queryClient.invalidateQueries({ queryKey: ["logistics", exerciseId] });
    },
    onError: (e: Error) => {
      setReverts((n) => n + 1);
      if (changedElsewhere(e)) {
        notify({ text: t("Modifié par un autre poste"), detail: e.message, tone: "warn" });
        void queryClient.invalidateQueries({ queryKey: ["logistics", exerciseId] });
        return;
      }
      notify({
        text: t("Hypothèse non enregistrée"),
        detail: t("{reason} Le curseur revient à la valeur enregistrée.", { reason: e.message }),
        tone: "warn",
      });
    },
  });
  return { save, reverts };
}

/** Hypothèses `keys` retouchées (différentes des valeurs par défaut du niveau). */
export function customOf(data: Logistics, keys: readonly (keyof LogisticsAssumptions)[]): boolean {
  return keys.some((k) => data.assumptions[k] !== data.defaults[k]);
}

/** Pastille « Propres au dossier » (avec l'auteur au survol) ou « Valeurs par défaut » de la ligne de titre. */
export function AssumptionsBadge({ data, custom }: { data: Logistics; custom: boolean }) {
  return custom ? (
    <Badge dot tone="accent" title={data.updated_by ? t("Réglées par {name}", { name: data.updated_by }) : undefined}>
      {t("Propres au dossier")}
    </Badge>
  ) : (
    <span className="text-meta text-ink-muted">{t("Valeurs par défaut")}</span>
  );
}

/** « Revenir aux valeurs par défaut » des hypothèses `keys` de la vue, partagé par tous les postes. */
export function ResetAssumptions({
  data,
  keys,
  custom,
  pending,
  onReset,
}: {
  data: Logistics;
  keys: readonly (keyof LogisticsAssumptions)[];
  custom: boolean;
  pending: boolean;
  onReset: (values: Partial<LogisticsAssumptions>) => void;
}) {
  const writeBlock = useWriteBlock();
  const defaults = Object.fromEntries(keys.map((k) => [k, data.defaults[k]])) as Partial<LogisticsAssumptions>;
  return (
    <>
      <p className="text-meta leading-snug text-ink-muted">{t("Partagées par tous les postes du dossier.")}</p>
      <div className="py-1">
        <Button variant="secondary" size="sm" disabled={!custom || pending} onClick={() => onReset(defaults)} {...writeBlock}>
          <RotateCcw size={14} /> {t("Revenir aux valeurs par défaut")}
        </Button>
      </div>
    </>
  );
}
